import { CircleAlert, MailPlus } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'

import { EmailChipInput } from '@/components/px/EmailChipInput'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { isValidEmail } from '@/features/clients/validate'
import {
  ASSIGNABLE_ORG_ROLES,
  INVITE_LOCALES,
  toInviteLocale,
  type AssignableOrgRole,
  type InviteAccessLevel,
  type InviteLocale,
} from '@/features/users/types'
import {
  selectAllUsers,
  selectUsersCache,
} from '@/features/users/usersSelectors'
import {
  fetchProjectInvites,
  fetchUsers,
  inviteProjectMember,
  projectInvitesRetried,
} from '@/features/users/usersSlice'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import type { ApiError } from '@/services'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

import { addMember } from '../slices/projectDetailSlice'

/** One send fans out one request per address; Exchange Online throttles a mailbox, so the dialog caps the batch. */
export const MAX_INVITES_PER_SEND = 20

const ACCESS_LEVELS: readonly InviteAccessLevel[] = ['WRITE', 'READ']

const selectClass =
  'w-full rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground min-h-9 max-sm:min-h-[44px]'

type OutcomeKind = 'sent' | 'duplicate' | 'orgMember' | 'added' | 'failed'

interface Outcome {
  email: string
  kind: OutcomeKind
  /** The server refusal behind a `failed` row. */
  error?: ApiError
  /** "Add to project" is running for this row. */
  adding?: boolean
}

const OUTCOME_TONE: Record<OutcomeKind, StatusTone> = {
  sent: 'done',
  duplicate: 'neutral',
  orgMember: 'review',
  added: 'done',
  failed: 'blocked',
}

const isForbidden = (error: ApiError | undefined): boolean =>
  error?.status === 403

/** Classify one refused invite by the server's key. */
const outcomeFor = (email: string, error: ApiError | undefined): Outcome => {
  if (error?.backendKey === 'users.invite.duplicate')
    return { email, kind: 'duplicate' }
  if (error?.backendKey === 'users.invite.already-org-member')
    return { email, kind: 'orgMember' }
  return { email, kind: 'failed', ...(error !== undefined ? { error } : {}) }
}

interface ProjectInviteDialogProps {
  orgId: string
  orgName: string
  projectId: string
  projectName: string
  /** Lets the pending list return focus here after the last row is revoked. */
  triggerRef?: RefObject<HTMLButtonElement | null>
  /** Every address was sent and the dialog closed; the panel announces it. */
  onAllSent: (sentCount: number, firstEmail: string) => void
  /** Hidden-for-everyone-else gate lives in the caller; this only disables mid-send dismissal. */
  triggerClassName?: string
}

/**
 * "Invite by e-mail" (TASK-7F3EE34E D1): a multi-address chip input, the
 * organisation role, the project access level and the e-mail language, then
 * one invitation per address. A full success closes the dialog; anything else
 * keeps it open with a per-address outcome, and only the addresses that can be
 * retried stay in the box. The first 403 stops the run. While a batch runs the
 * dialog cannot be dismissed. The caller decides who sees the trigger; the
 * server remains the gate (org admin + project WRITE).
 */
export function ProjectInviteDialog({
  orgId,
  orgName,
  projectId,
  projectName,
  triggerRef,
  onAllSent,
  triggerClassName,
}: ProjectInviteDialogProps) {
  const { t } = useTranslation('projectWorkspace')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const emailInputRef = useRef<HTMLInputElement>(null)

  const onOpenChanged = (next: boolean): void => {
    if (!next && busy) return
    setOpen(next)
  }

  const onAllSentAndClose = (sentCount: number, firstEmail: string): void => {
    setOpen(false)
    onAllSent(sentCount, firstEmail)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChanged}>
      <DialogTrigger ref={triggerRef} asChild>
        <Button
          type="button"
          variant="outline"
          data-testid="project-invite-open"
          className={
            triggerClassName ??
            'h-7 px-2.5 text-xs max-sm:h-[44px] max-sm:w-full'
          }
        >
          <MailPlus aria-hidden className="size-3.5" />
          {t('members.inviteOpen')}
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="project-invite-dialog"
        closeDisabled={busy}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          emailInputRef.current?.focus()
        }}
        onEscapeKeyDown={(event) => {
          if (busy) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (busy) event.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('members.invite.title')}</DialogTitle>
          <DialogDescription>
            {t('members.invite.description', {
              org: orgName,
              project: projectName,
            })}
          </DialogDescription>
        </DialogHeader>
        <InviteBody
          orgId={orgId}
          orgName={orgName}
          projectId={projectId}
          emailInputRef={emailInputRef}
          onBusyChanged={setBusy}
          onAllSent={onAllSentAndClose}
          onCloseClicked={() => onOpenChanged(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

interface InviteBodyProps {
  orgId: string
  orgName: string
  projectId: string
  emailInputRef: RefObject<HTMLInputElement | null>
  onBusyChanged: (busy: boolean) => void
  onAllSent: (sentCount: number, firstEmail: string) => void
  onCloseClicked: () => void
}

/** The form + outcomes. Mounted only while the dialog is open, so every open starts fresh. */
function InviteBody({
  orgId,
  orgName,
  projectId,
  emailInputRef,
  onBusyChanged,
  onAllSent,
  onCloseClicked,
}: InviteBodyProps) {
  const { t, i18n } = useTranslation([
    'projectWorkspace',
    'peopleAdmin',
    'common',
    'nav',
  ])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()
  const emailsId = useId()
  const hintId = useId()
  const errorId = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const alertRef = useRef<HTMLDivElement>(null)
  const mounted = useRef(true)

  const [emails, setEmails] = useState<string[]>([])
  const [role, setRole] = useState<AssignableOrgRole>('member')
  const [access, setAccess] = useState<InviteAccessLevel>('WRITE')
  const [locale, setLocale] = useState<InviteLocale>(() =>
    toInviteLocale(i18n.resolvedLanguage)
  )
  const [sending, setSending] = useState<{
    done: number
    count: number
  } | null>(null)
  const [outcomes, setOutcomes] = useState<Outcome[]>([])
  const [forbidden, setForbidden] = useState(false)
  const [formError, setFormError] = useState<{
    key: string
    count?: number
  } | null>(null)

  const users = useAppSelector(selectAllUsers)
  const usersCache = useAppSelector(selectUsersCache)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const hasOutcomes = outcomes.length > 0
  useEffect(() => {
    if (hasOutcomes && sending === null && !forbidden)
      headingRef.current?.focus()
  }, [hasOutcomes, sending, forbidden])
  useEffect(() => {
    if (forbidden) alertRef.current?.focus()
  }, [forbidden])

  const invalidEmails = emails.filter((email) => !isValidEmail(email))

  const onEmailsChanged = useCallback((next: string[]): void => {
    setEmails(next)
    setFormError(null)
  }, [])

  /** Why the box cannot be sent as it stands, or null when it can. */
  const formProblem = (): { key: string; count?: number } | null => {
    if (emails.length === 0)
      return { key: 'members.invite.errors.emailsRequired' }
    if (invalidEmails.length > 0)
      return {
        key: 'members.invite.errors.emailsInvalid',
        count: invalidEmails.length,
      }
    if (emails.length > MAX_INVITES_PER_SEND)
      return { key: 'members.invite.errors.tooMany' }
    return null
  }

  /** The org directory row for an address, matched ignoring case. */
  const userByEmail = (email: string) =>
    users.find(
      (candidate) => candidate.email.toLowerCase() === email.toLowerCase()
    )

  const onSubmitClicked = async (): Promise<void> => {
    if (sending !== null) return
    const problem = formProblem()
    if (problem !== null) {
      setFormError(problem)
      emailInputRef.current?.focus()
      return
    }

    setFormError(null)
    setForbidden(false)
    setOutcomes([])
    onBusyChanged(true)
    const batch = emails
    const results: Outcome[] = []
    let stoppedAt = batch.length
    for (const [index, email] of batch.entries()) {
      if (!mounted.current) return
      setSending({ done: index, count: batch.length })
      const action = await dispatch(
        inviteProjectMember({
          projectId,
          input: { email, role, accessLevel: access, locale },
        })
      )
      if (inviteProjectMember.fulfilled.match(action)) {
        results.push({ email, kind: 'sent' })
        continue
      }
      const error = action.payload
      if (isForbidden(error)) {
        stoppedAt = index
        break
      }
      results.push(outcomeFor(email, error))
    }
    if (!mounted.current) return

    const sent = results.filter((row) => row.kind === 'sent')
    const orgMembers = results.some((row) => row.kind === 'orgMember')
    if (sent.length > 0) {
      dispatch(projectInvitesRetried(projectId))
      void dispatch(fetchProjectInvites(projectId))
    }
    if (orgMembers) void dispatch(fetchUsers())

    setSending(null)
    onBusyChanged(false)
    if (stoppedAt < batch.length) {
      setForbidden(true)
      setOutcomes(results)
      setEmails(batch.slice(stoppedAt))
      return
    }
    if (results.every((row) => row.kind === 'sent')) {
      onAllSent(sent.length, sent[0]?.email ?? '')
      return
    }
    setOutcomes(results)
    // Only the addresses that can be retried stay in the box.
    setEmails(
      results.filter((row) => row.kind === 'failed').map((row) => row.email)
    )
  }

  const onAddToProjectClicked = async (email: string): Promise<void> => {
    const user = userByEmail(email)
    if (user?.idHex === undefined) return
    setOutcomes((rows) =>
      rows.map((row) => (row.email === email ? { ...row, adding: true } : row))
    )
    const action = await dispatch(
      addMember({
        orgId,
        projectId,
        input: { userId: user.idHex, accessLevel: access },
      })
    )
    if (!mounted.current) return
    const added = addMember.fulfilled.match(action)
    setOutcomes((rows) =>
      rows.map((row) =>
        row.email === email
          ? added
            ? { email, kind: 'added' }
            : { email, kind: 'failed' }
          : row
      )
    )
  }

  const sentCount = outcomes.filter((row) => row.kind === 'sent').length
  const submitCount = Math.max(emails.length, 1)
  const isSending = sending !== null
  const directoryReady = !usersCache.loading && !usersCache.invalidated

  const failureText = (error: ApiError | undefined): string =>
    error !== undefined &&
    (error.backendKey !== undefined || error.status === 0)
      ? (toMessage(error) ?? t('members.invite.outcome.failed'))
      : t('members.invite.outcome.failed')

  return (
    <>
      {forbidden && (
        <div
          ref={alertRef}
          tabIndex={-1}
          role="alert"
          data-testid="project-invite-forbidden"
          className="flex gap-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground"
        >
          <CircleAlert
            aria-hidden
            className="mt-0.5 size-4 shrink-0 text-blocked"
          />
          <span className="min-w-0">
            {t('members.invite.errors.forbidden')}
          </span>
        </div>
      )}

      {hasOutcomes && (
        <section
          aria-labelledby={`${emailsId}-outcome`}
          data-testid="project-invite-outcome"
        >
          <h3
            id={`${emailsId}-outcome`}
            ref={headingRef}
            tabIndex={-1}
            role="status"
            className="inline-block rounded-sm text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-ink"
          >
            {t('members.invite.outcome.heading', {
              sent: sentCount,
              count: outcomes.length,
            })}
          </h3>
          <ul className="mt-2 divide-y divide-border">
            {outcomes.map((row) => {
              const resolvable = userByEmail(row.email)?.idHex !== undefined
              return (
                <li
                  key={row.email}
                  data-testid="project-invite-outcome-row"
                  data-outcome={row.kind}
                  className="grid gap-x-2 gap-y-1 py-2 sm:grid-cols-[minmax(0,1fr)_auto]"
                >
                  <span className="min-w-0 break-all text-sm font-medium text-foreground">
                    {row.email}
                  </span>
                  <span>
                    <StatusPill tone={OUTCOME_TONE[row.kind]}>
                      {t(`members.invite.outcome.status.${row.kind}`)}
                    </StatusPill>
                  </span>
                  {row.kind === 'duplicate' && (
                    <p className="text-xs text-muted-foreground sm:col-span-2">
                      {t('members.invite.outcome.duplicate')}
                    </p>
                  )}
                  {row.kind === 'orgMember' && (
                    <>
                      <p className="text-xs text-muted-foreground sm:col-span-2">
                        {directoryReady && !resolvable
                          ? t('members.invite.outcome.addUnresolved', {
                              org: orgName,
                            })
                          : t('members.invite.outcome.orgMember', {
                              org: orgName,
                            })}
                      </p>
                      {(resolvable || !directoryReady) && (
                        <span className="sm:col-span-2">
                          <Button
                            type="button"
                            variant="outline"
                            data-testid="project-invite-add-instead"
                            aria-busy={row.adding || undefined}
                            aria-disabled={
                              row.adding || !resolvable || undefined
                            }
                            disabled={!resolvable && !row.adding}
                            onClick={() => {
                              if (!row.adding)
                                void onAddToProjectClicked(row.email)
                            }}
                            className="h-7 px-2.5 text-xs max-sm:h-[44px] max-sm:w-full"
                          >
                            {row.adding
                              ? t('members.invite.outcome.adding')
                              : t('members.invite.outcome.addInstead', {
                                  access: t(`members.access.${access}`),
                                })}
                          </Button>
                        </span>
                      )}
                    </>
                  )}
                  {row.kind === 'added' && (
                    <p
                      role="status"
                      className="text-xs text-muted-foreground sm:col-span-2"
                    >
                      {t('members.invite.outcome.added', {
                        access: t(`members.access.${access}`),
                      })}
                    </p>
                  )}
                  {row.kind === 'failed' && (
                    <p className="text-xs text-muted-foreground sm:col-span-2">
                      {failureText(row.error)}
                    </p>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      <fieldset
        disabled={isSending}
        className="grid min-w-0 gap-4 border-0 p-0"
      >
        <div>
          <label
            htmlFor={emailsId}
            className="block text-sm font-medium text-foreground"
          >
            {t('members.invite.emailsLabel')}
          </label>
          <div className="mt-1">
            <EmailChipInput
              id={emailsId}
              emails={emails}
              onEmailsChanged={onEmailsChanged}
              invalid={invalidEmails}
              describedBy={formError !== null ? `${hintId} ${errorId}` : hintId}
              placeholder={t('members.invite.emailsPlaceholder')}
              disabled={isSending}
              inputTestId="project-invite-email-input"
              inputRef={emailInputRef}
            />
          </div>
          <p id={hintId} className="mt-1 text-xs text-muted-foreground">
            {hasOutcomes
              ? t('members.invite.retryHint')
              : t('members.invite.emailsHint')}
          </p>
          {formError !== null && (
            <p
              id={errorId}
              role="alert"
              data-testid="project-invite-form-error"
              className="mt-1 flex gap-1.5 text-xs text-blocked"
            >
              <CircleAlert aria-hidden className="mt-px size-3.5 shrink-0" />
              <span>
                {t(formError.key, {
                  count: formError.count ?? 1,
                  max: MAX_INVITES_PER_SEND,
                })}
              </span>
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor={`${emailsId}-role`}
            className="block text-sm font-medium text-foreground"
          >
            {t('members.invite.roleLabel')}
          </label>
          <select
            id={`${emailsId}-role`}
            value={role}
            data-testid="project-invite-role"
            onChange={(event) =>
              setRole(event.target.value as AssignableOrgRole)
            }
            className={cn('mt-1', selectClass)}
          >
            {ASSIGNABLE_ORG_ROLES.map((option) => (
              <option key={option} value={option}>
                {t(`peopleAdmin:roles.${option}`)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(`peopleAdmin:roleHints.${role}`)}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor={`${emailsId}-access`}
              className="block text-sm font-medium text-foreground"
            >
              {t('members.invite.accessLabel')}
            </label>
            <select
              id={`${emailsId}-access`}
              value={access}
              data-testid="project-invite-access"
              onChange={(event) =>
                setAccess(event.target.value as InviteAccessLevel)
              }
              className={cn('mt-1', selectClass)}
            >
              {ACCESS_LEVELS.map((option) => (
                <option key={option} value={option}>
                  {t(`members.access.${option}`)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {t(`members.invite.accessHint.${access}`)}
            </p>
          </div>
          <div>
            <label
              htmlFor={`${emailsId}-locale`}
              className="block text-sm font-medium text-foreground"
            >
              {t('members.invite.localeLabel')}
            </label>
            <select
              id={`${emailsId}-locale`}
              value={locale}
              data-testid="project-invite-locale"
              onChange={(event) =>
                setLocale(event.target.value as InviteLocale)
              }
              className={cn('mt-1', selectClass)}
            >
              {INVITE_LOCALES.map((option) => (
                <option key={option} value={option}>
                  {t(`nav:language.${option}`)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('members.invite.localeHint')}
            </p>
          </div>
        </div>
      </fieldset>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          disabled={isSending}
          onClick={onCloseClicked}
        >
          {hasOutcomes && sentCount > 0
            ? t('common:actionsMore.close')
            : t('common:actionsMore.cancel')}
        </Button>
        <Button
          type="button"
          data-testid="project-invite-submit"
          aria-busy={isSending || undefined}
          aria-disabled={isSending || undefined}
          onClick={() => void onSubmitClicked()}
          className="aria-busy:cursor-progress"
        >
          {sending !== null
            ? t('members.invite.sending', {
                done: sending.done + 1,
                count: sending.count,
              })
            : t('members.invite.submit', { count: submitCount })}
        </Button>
      </DialogFooter>
    </>
  )
}
